import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { makeApiCall } from "src/api/apiRequests";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import ValidationMessage from "src/helpers/ValidationMessage";
import FLASH_MESSAGES from "src/helpers/FlashMessages";

interface AddNewAgeGroupProps {
  onShowModal: boolean;
  setShowModal: (visible: boolean) => void;
  setModified: (visible: boolean) => void;
}

const AddNewAgeGroup: React.FC<AddNewAgeGroupProps> = ({
  onShowModal,
  setShowModal,
  setModified,
}) => {
  const { t } = useTranslation();

  const [from, setFrom] = useState<any>();
  const [to, setTo] = useState<any>();

  const [loadingButton, setLoadingButton] = useState(false);
  const [validations, setValidations] = useState<Record<string, string>>({});

  const onClose = () => {
    setShowModal(false);
  };

  const addAgeGroup = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoadingButton(true);

    const formData = {
      from: from,
      to: to,
    };

    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.educationAgeGroupStore,
        "POST",
        API_HEADERS.authenticated,
        formData
      );

      FLASH_MESSAGES.successMsg(response.success);

      setModified(true);

      onClose();

      setLoadingButton(false);
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        onClose();
      }
      setLoadingButton(false);
    }
  };

  return (
    <Modal isOpen={onShowModal} toggle={() => onClose()} size="md" centered>
      <ModalHeader
        className="text-dark font-size-15"
        toggle={() => onClose()}
        tag="span"
      >
        {t("education_add_age_group_title")}
      </ModalHeader>
      <form onSubmit={(e) => addAgeGroup(e)}>
        <ModalBody>
          <div className="modal-note">{t("education_add_age_group_desc")}</div>
          <div className="row">
            <div className="form-block col-12 col-md-6">
              <label className="required">
                {t("education_add_age_group_from_label")}
              </label>
              <input
                type="number"
                name="name"
                placeholder={t("education_add_age_group_from_placeholder")}
                value={from}
                className={
                  "form-input" + (validations.from ? " input-error" : "")
                }
                autoComplete="off"
                onChange={(e) => setFrom(e.target.value)}
              />
              {validations.from && (
                <ValidationMessage message={validations.from[0]} />
              )}
            </div>
            <div className="form-block col-12 col-md-6">
              <label className="required">
                {t("education_add_age_group_to_label")}
              </label>
              <input
                type="number"
                name="name"
                min={(parseInt(from) + 1).toString()}
                placeholder={t("education_add_age_group_to_placeholder")}
                value={to}
                className={
                  "form-input" + (validations.to ? " input-error" : "")
                }
                autoComplete="off"
                onChange={(e) => setTo(e.target.value)}
              />
              {validations.to && (
                <ValidationMessage message={validations.to[0]} />
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="button-white"
            type="button"
            onClick={() => onClose()}
          >
            {t("education_add_age_group_cancel")}
          </button>

          {loadingButton ? (
            <button type="button" className="button-blue has-icon">
              <i className="spinner-icon" />
              {t("education_add_age_group_adding_button")}
            </button>
          ) : (
            <button type="submit" className="button-blue has-icon">
              <i className="icon add-icon" />
              {t("education_add_age_group_add_button")}
            </button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddNewAgeGroup;
